import { Container, Text, TextStyle, Graphics, FillGradient, Sprite, Assets } from 'pixi.js';
import gsap from 'gsap';
import { BaseScene } from './BaseScene';
import { UIButton } from '../components/UIButton';
import { PlayerResultCard } from '../components/PlayerResultCard';
import { COLORS, FONTS, SPACING } from '../constants/theme';
import type { LayoutMetrics } from '../LayoutManager';
import type { GameState } from '@/types';

export interface GameOverSceneOptions {
  getState: () => GameState;
  onPlayAgain: () => void;  // Resets warriors and transitions to coin flip
  onStartOver: () => void;  // Goes back to title screen
}

interface ConfettiParticle {
  graphics: Graphics;
  vx: number;
  vy: number;
  rotation: number;
  rotationSpeed: number;
  life: number;
}

// Card aspect ratio
const CARD_ASPECT_RATIO = 280 / 380;

/**
 * Game over scene with victory announcement and player result cards
 * Uses Layout v3 flexbox for responsive layout
 */
export class GameOverScene extends BaseScene {
  private metrics: LayoutMetrics;
  private options: GameOverSceneOptions;
  private backgroundSprite: Sprite | null = null;
  private rootLayout: Container | null = null;
  private headerContainer: Container | null = null;
  private cardsContainer: Container | null = null;
  private buttonsContainer: Container | null = null;
  private victoryText: Text | null = null;
  private winnerText: Text | null = null;
  private player1Card: PlayerResultCard | null = null;
  private player2Card: PlayerResultCard | null = null;
  private playAgainButton: UIButton | null = null;
  private startOverButton: UIButton | null = null;
  private confettiParticles: ConfettiParticle[] = [];
  private confettiInterval: number | null = null;

  constructor(metrics: LayoutMetrics, options: GameOverSceneOptions) {
    super();
    this.metrics = metrics;
    this.options = options;
    this.build();
  }

  private async loadBackground(): Promise<void> {
    try {
      const backgroundTexture = await Assets.load('/assets/background.png');
      this.backgroundSprite = new Sprite(backgroundTexture);
      this.backgroundSprite.anchor.set(0, 0);
    } catch (error) {
      console.error('Failed to load background:', error);
    }
  }

  protected build(): void {
    // Root layout - column with header, cards, and buttons
    this.rootLayout = new Container({
      layout: {
        width: this.metrics.width,
        height: this.metrics.height,
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: SPACING.XL,
        paddingBottom: SPACING.XL,
      },
    });
    this.container.addChild(this.rootLayout);

    // Header container for victory and winner text
    this.headerContainer = new Container({
      layout: {
        flexDirection: 'column',
        alignItems: 'center',
        gap: SPACING.SM,
      },
    });
    this.rootLayout.addChild(this.headerContainer);

    // Victory text with gradient
    const victoryGradient = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 },
      end: { x: 0, y: 1 },
      colorStops: [
        { offset: 0, color: COLORS.PLAYER1 },
        { offset: 0.5, color: COLORS.WARNING },
        { offset: 1, color: COLORS.PLAYER1 },
      ],
    });

    const victoryStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: FONTS.SIZES.TITLE,
      fontWeight: 'bold',
      fill: victoryGradient,
      dropShadow: {
        color: 0x000000,
        blur: 10,
        distance: 4,
      },
    });

    this.victoryText = new Text({ text: 'VICTORY!', style: victoryStyle });
    this.victoryText.anchor.set(0.5);
    this.victoryText.layout = true;
    this.headerContainer.addChild(this.victoryText);

    // Winner text (will be set in onEnter)
    const winnerStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: FONTS.SIZES.XL,
      fill: COLORS.TEXT_PRIMARY,
    });

    this.winnerText = new Text({ text: '', style: winnerStyle });
    this.winnerText.anchor.set(0.5);
    this.winnerText.layout = true;
    this.headerContainer.addChild(this.winnerText);

    // Cards container - direction changes based on orientation
    this.cardsContainer = new Container({
      layout: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: SPACING.XL,
      },
    });
    this.rootLayout.addChild(this.cardsContainer);

    // Buttons container - center the buttons
    const buttonWidth = 200;
    this.buttonsContainer = new Container({
      layout: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: SPACING.LG,
      },
    });
    this.rootLayout.addChild(this.buttonsContainer);

    // Play again button - wrap in container for flexbox centering
    const buttonHeight = 48;
    const playAgainWrapper = new Container({
      layout: { width: buttonWidth, height: buttonHeight },
    });
    this.playAgainButton = new UIButton({
      text: 'Play Again',
      width: buttonWidth,
      variant: 'primary',
      onClick: () => this.handlePlayAgain(),
    });
    this.playAgainButton.x = buttonWidth / 2;
    this.playAgainButton.y = buttonHeight / 2;
    playAgainWrapper.addChild(this.playAgainButton);
    this.buttonsContainer.addChild(playAgainWrapper);

    // Start over button - wrap in container for flexbox centering
    const startOverWrapper = new Container({
      layout: { width: buttonWidth, height: buttonHeight },
    });
    this.startOverButton = new UIButton({
      text: 'Start Over',
      width: buttonWidth,
      variant: 'secondary',
      onClick: this.options.onStartOver,
    });
    this.startOverButton.x = buttonWidth / 2;
    this.startOverButton.y = buttonHeight / 2;
    startOverWrapper.addChild(this.startOverButton);
    this.buttonsContainer.addChild(startOverWrapper);
  }

  private calculateCardDimensions(): {
    cardWidth: number;
    cardHeight: number;
    stackVertically: boolean;
  } {
    const { width, height, mode } = this.metrics;
    const isPortrait = mode === 'portrait';

    // Available space for cards (leaving room for header and buttons)
    const availableWidth = width - SPACING.LG * 2;
    const availableHeight = height * 0.55;

    let cardWidth: number;
    let cardHeight: number;
    const cardGap = SPACING.XL;
    let stackVertically = false;

    if (isPortrait) {
      // In portrait, check if we can fit 2 cards side by side
      const twoCardsWidth = (availableWidth - cardGap) / 2;
      const twoCardsHeight = twoCardsWidth / CARD_ASPECT_RATIO;

      if (twoCardsHeight <= availableHeight && twoCardsWidth >= 120) {
        cardWidth = twoCardsWidth;
        cardHeight = twoCardsHeight;
      } else {
        stackVertically = true;
        cardHeight = (availableHeight - cardGap) / 2;
        cardWidth = cardHeight * CARD_ASPECT_RATIO;
        cardWidth = Math.min(cardWidth, availableWidth);
        cardHeight = cardWidth / CARD_ASPECT_RATIO;
      }
    } else {
      // Landscape: cards side by side - make them bigger
      // Use more of the available height
      cardHeight = Math.min(availableHeight * 0.95, 450);
      cardWidth = cardHeight * CARD_ASPECT_RATIO;

      const totalWidth = cardWidth * 2 + cardGap;
      if (totalWidth > availableWidth) {
        cardWidth = (availableWidth - cardGap) / 2;
        cardHeight = cardWidth / CARD_ASPECT_RATIO;
      }
    }

    // Enforce minimum size
    cardWidth = Math.max(cardWidth, 220);
    cardHeight = cardWidth / CARD_ASPECT_RATIO;

    return { cardWidth, cardHeight, stackVertically };
  }

  private createPlayerCards(): void {
    const state = this.options.getState();
    const changes = state.ratingChanges;
    const isAI = state.gameMode === '1-player';

    // Calculate responsive card dimensions
    const { cardWidth, cardHeight, stackVertically } = this.calculateCardDimensions();

    // Clear existing cards from container
    if (this.cardsContainer) {
      this.cardsContainer.removeChildren();
    }

    // Update cards container direction
    if (this.cardsContainer) {
      this.cardsContainer.layout = {
        flexDirection: stackVertically ? 'column' : 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: SPACING.XL,
      };
    }

    // Create Player 1 card
    this.player1Card = new PlayerResultCard({
      player: state.players.player1,
      ratingDelta: changes?.player1 ?? 0,
      isAI: false,
      width: cardWidth,
      height: cardHeight,
    });
    this.player1Card.layout = { width: cardWidth, height: cardHeight };
    this.cardsContainer!.addChild(this.player1Card);

    // Create Player 2 card
    this.player2Card = new PlayerResultCard({
      player: state.players.player2,
      ratingDelta: changes?.player2 ?? 0,
      isAI: isAI,
      width: cardWidth,
      height: cardHeight,
    });
    this.player2Card.layout = { width: cardWidth, height: cardHeight };
    this.cardsContainer!.addChild(this.player2Card);
  }

  private updateLayout(): void {
    const { width, height } = this.metrics;
    const { stackVertically } = this.calculateCardDimensions();

    // Update root layout
    if (this.rootLayout) {
      this.rootLayout.layout = {
        width,
        height,
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: SPACING.XL,
        paddingBottom: SPACING.XL,
      };
    }

    // Update cards container direction
    if (this.cardsContainer) {
      this.cardsContainer.layout = {
        flexDirection: stackVertically ? 'column' : 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: SPACING.XL,
      };
    }
  }

  private spawnConfetti(): void {
    const colors = [COLORS.PLAYER1, COLORS.PLAYER2, COLORS.SUCCESS, COLORS.WARNING, 0xff69b4];

    for (let i = 0; i < 3; i++) {
      const x = Math.random() * this.metrics.width;
      const color = colors[Math.floor(Math.random() * colors.length)];

      const graphics = new Graphics();
      graphics.rect(-4, -8, 8, 16);
      graphics.fill({ color });
      graphics.x = x;
      graphics.y = -20;

      // Add confetti to main container (not rootLayout) so it's behind/outside flexbox
      this.container.addChildAt(graphics, 0);

      const particle: ConfettiParticle = {
        graphics,
        vx: (Math.random() - 0.5) * 4,
        vy: 2 + Math.random() * 3,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.2,
        life: 200,
      };

      this.confettiParticles.push(particle);
    }
  }

  onUpdate(delta: number): void {
    // Update confetti
    for (let i = this.confettiParticles.length - 1; i >= 0; i--) {
      const p = this.confettiParticles[i];

      p.graphics.x += p.vx * delta;
      p.graphics.y += p.vy * delta;
      p.graphics.rotation += p.rotationSpeed * delta;
      p.life -= delta;

      // Add some sway
      p.vx += (Math.random() - 0.5) * 0.1;

      // Remove if off screen or dead
      if (p.graphics.y > this.metrics.height + 20 || p.life <= 0) {
        p.graphics.destroy();
        this.confettiParticles.splice(i, 1);
      }
    }
  }

  async onEnter(): Promise<void> {
    super.onEnter();

    // Load background and add to container
    await this.loadBackground();
    if (this.backgroundSprite) {
      this.backgroundSprite.width = this.metrics.width;
      this.backgroundSprite.height = this.metrics.height;
      this.container.addChildAt(this.backgroundSprite, 0);
    }

    // Get winner info
    const state = this.options.getState();
    const isTie = state.winner === 'tie';
    const winnerName = isTie
      ? "It's a Tie!"
      : state.winner === 'player1'
        ? state.players.player1.name
        : state.players.player2.name;
    const winnerColor = isTie
      ? COLORS.TEXT_SECONDARY
      : state.winner === 'player1'
        ? COLORS.PLAYER1
        : COLORS.PLAYER2;

    // Update winner text
    if (this.winnerText) {
      this.winnerText.text = isTie ? "It's a Tie!" : `${winnerName} Wins!`;
      this.winnerText.style.fill = winnerColor;
    }

    // Update victory text for tie
    if (this.victoryText && isTie) {
      this.victoryText.text = 'Draw';
    } else if (this.victoryText) {
      this.victoryText.text = 'Victory!';
    }

    // Create player cards with current state
    this.createPlayerCards();

    // Animate elements in
    if (this.victoryText) {
      this.victoryText.alpha = 0;
      this.victoryText.scale.set(0.5);
      gsap.to(this.victoryText, { alpha: 1, duration: 0.5 });
      gsap.to(this.victoryText.scale, { x: 1, y: 1, duration: 0.5, ease: 'back.out(1.7)' });
    }

    if (this.winnerText) {
      this.winnerText.alpha = 0;
      gsap.to(this.winnerText, { alpha: 1, duration: 0.5, delay: 0.2 });
    }

    if (this.player1Card) {
      this.player1Card.alpha = 0;
      gsap.to(this.player1Card, { alpha: 1, duration: 0.5, delay: 0.3 });
    }

    if (this.player2Card) {
      this.player2Card.alpha = 0;
      gsap.to(this.player2Card, { alpha: 1, duration: 0.5, delay: 0.4 });
    }

    if (this.playAgainButton) {
      this.playAgainButton.alpha = 0;
      gsap.to(this.playAgainButton, { alpha: 1, duration: 0.5, delay: 0.5 });
    }

    if (this.startOverButton) {
      this.startOverButton.alpha = 0;
      gsap.to(this.startOverButton, { alpha: 1, duration: 0.5, delay: 0.55 });
    }

    // Start confetti
    this.confettiInterval = window.setInterval(() => this.spawnConfetti(), 100);
  }

  onExit(): void {
    super.onExit();

    // Stop confetti
    if (this.confettiInterval) {
      clearInterval(this.confettiInterval);
      this.confettiInterval = null;
    }

    // Clear existing confetti
    for (const p of this.confettiParticles) {
      p.graphics.destroy();
    }
    this.confettiParticles = [];
  }

  onResize(metrics: LayoutMetrics): void {
    this.metrics = metrics;

    // Update background size
    if (this.backgroundSprite) {
      this.backgroundSprite.width = metrics.width;
      this.backgroundSprite.height = metrics.height;
    }

    // Update layout and recreate cards
    this.updateLayout();
    if (this.isActive) {
      this.createPlayerCards();
    }
  }

  /**
   * Handle Play Again button click
   */
  private handlePlayAgain(): void {
    // Stop confetti before transitioning
    if (this.confettiInterval) {
      clearInterval(this.confettiInterval);
      this.confettiInterval = null;
    }

    // Clear confetti particles
    for (const p of this.confettiParticles) {
      p.graphics.destroy();
    }
    this.confettiParticles = [];

    // Transition to coin flip scene
    this.options.onPlayAgain();
  }

  dispose(): void {
    if (this.confettiInterval) {
      clearInterval(this.confettiInterval);
    }
    for (const p of this.confettiParticles) {
      p.graphics.destroy();
    }
    super.dispose();
  }
}
